import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { logError } from "../../../lib/logging"
import { PlayerState, TimetableState } from "../../../lib/types"
import { useTimetableStore } from "../../timetable/lib/useTimetableStore"

import { AnnouncementAudio } from "./announcementAudio"
import { createTicker } from "./createTicker"

// An announcement we only learn about after it was due (page opened late, socket reconnected)
// still plays, as long as it is not older than this.
const CATCH_UP_MS = 60_000

// Booking moments land on the minute, and so do Pathe's own announcements over the same speakers.
// Ours are held back a little so the two do not talk over each other.
const ANNOUNCEMENT_OFFSET_MS = 15_000

// How long a finished booking stays on the list.
const KEEP_ON_SCREEN_MS = 15 * 60_000

// The highest room number `scripts/generate-announcement-audio.mjs` produces a clip for. Rooms
// above it fall back to the clip that does not name a cube.
const HIGHEST_ROOM_NUMBER_WITH_A_CLIP = 12

const TEST_CLIP_URL = "/audio/announcements/test.mp3"

// Sounded before every announcement, so a room hears something is coming before the words start.
const CHIME_URL = "/audio/announcements/chime.mp3"

const VOLUME_STORAGE_KEY = "announcements.volume"
const MUTED_STORAGE_KEY = "announcements.muted"
const ENABLED_STORAGE_KEY = "announcements.enabled"

const DEFAULT_ENABLED: EnabledAnnouncements = {
  start: true,
  filmEnd: true,
  end: true,
}

// The three moments that can be announced, each switchable on its own.
//
// `start` and `end` come from the booking: its start, and its unrounded end - film plus trailer
// time, the same moment the guest is told about on the ticket. Not the slot end, which is rounded
// up to the next quarter.
//
// `filmEnd` comes from the projector rather than the plan. While a film runs it reports how much
// is left, which gives a moment that is drawn in ahead of time and refined with every reading;
// when it reports it has stopped, that is the last word. Both are recomputed on every tick, so a
// moment that moves costs nothing - it is simply read again.
export type AnnouncementType = "start" | "filmEnd" | "end"

export const ANNOUNCEMENT_TYPES: AnnouncementType[] = ["start", "filmEnd", "end"]

export const ANNOUNCEMENT_LABELS: Record<AnnouncementType, string> = {
  start: "start",
  filmEnd: "film end",
  end: "end",
}

const CLIP_NAMES: Record<AnnouncementType, string> = {
  start: "start",
  filmEnd: "film-end",
  end: "end",
}

// A film that has begun, whether or not it is moving at this instant.
const PLAYING_STATES = ["play", "pause"]

// What the projector reports once a film is over. Anything else - buffering, positioning - says
// nothing either way.
const STOPPED_STATES = ["stop", "cleared"]

// The only state in which the remaining time is actually running down. While a film is paused it
// stands still, so counting from it would land an announcement in the middle of the pause.
const ADVANCING_STATE = "play"

// A film may legitimately run past the end of its slot, but not by a lot. A reading that puts the
// end outside this window is treated as nonsense rather than announced.
const MAX_OVERRUN_MS = 60 * 60_000

// `queued` means the clip is waiting for another room's announcement to finish - a matter of
// seconds, not the minutes-ahead scheduling this page used to do.
export type AnnouncementStatus = "pending" | "queued" | "played" | "muted" | "missed" | "off"

export type Announcement = {
  at: Date
  status: AnnouncementStatus
}

export type BookingRow = {
  bookingId: string
  roomNumber: number
  roomName: string
  productName: string
  userGivenName: string
  isRunning: boolean
  start: Announcement
  // Absent until the projector reports it has stopped.
  filmEnd?: Announcement
  end: Announcement
}

export type EnabledAnnouncements = Record<AnnouncementType, boolean>

// What the page remembers about a booking, independently of whether the socket still mentions it.
type KnownBooking = {
  bookingId: string
  roomNumber: number
  roomName: string
  productName: string
  userGivenName: string
  bookingFrom: Date
  bookingUntil: Date
  bookingUntilNotRounded?: Date
}

type PlayedAnnouncement = {
  muted: boolean
  // Wall-clock moment the clip actually starts sounding, which is later than the moment it fell
  // due when it has to wait for another room's clip. Absent until the clip has been handed over,
  // so the list never claims an announcement that has not been spoken.
  startsAt?: number
}

function getClipUrl(type: AnnouncementType, roomNumber: number): string {
  const clip = CLIP_NAMES[type]

  if (roomNumber < 1 || roomNumber > HIGHEST_ROOM_NUMBER_WITH_A_CLIP) {
    return `/audio/announcements/${clip}.mp3`
  }

  return `/audio/announcements/${clip}.room-${String(roomNumber).padStart(2, "0")}.mp3`
}

function getAnnouncementKey(bookingId: string, type: AnnouncementType): string {
  return `${bookingId}:${type}`
}

// The end a guest is told about: the booking's unrounded end, which is the film plus trailer
// time. Older bookings that the API cannot work it out for fall back to the slot end, so an
// announcement still happens - just at the rounded moment.
/**
 * When the film itself is over. A projector that has stopped settles it; one that is still
 * running gives the time it expects to finish, counted down from the reading rather than from
 * now, since the next one is two minutes away. A reading that lands outside the booking is
 * ignored rather than announced.
 *
 * Needs neither the offset nor a fallback: it never lands on the minute, and until the projector
 * says something there is nothing to announce.
 */
function getFilmEnd(
  booking: KnownBooking,
  stoppedAt: number | undefined,
  player: PlayerState | undefined,
): Date | undefined {
  if (stoppedAt !== undefined) {
    return new Date(stoppedAt)
  }

  // Only worth extrapolating while the film is actually moving. Paused, the remaining time stays
  // put while the clock does not, so a pause with less left than the two minutes to the next
  // reading would otherwise announce the end partway through it - and once announced, the real
  // end can never follow.
  if (!player || player.remainingSeconds === undefined || player.state !== ADVANCING_STATE) {
    return undefined
  }

  const expected = player.receivedAt + player.remainingSeconds * 1000
  const isSane =
    expected >= booking.bookingFrom.getTime() &&
    expected <= booking.bookingUntil.getTime() + MAX_OVERRUN_MS

  return isSane ? new Date(expected) : undefined
}

function getMoment(
  booking: KnownBooking,
  type: AnnouncementType,
  filmEndedAt: Record<string, number>,
  playerStates: Record<string, PlayerState>,
): Date | undefined {
  if (type === "filmEnd") {
    return getFilmEnd(booking, filmEndedAt[booking.bookingId], playerStates[booking.bookingId])
  }

  const moment =
    type === "start" ? booking.bookingFrom : booking.bookingUntilNotRounded ?? booking.bookingUntil

  return new Date(moment.getTime() + ANNOUNCEMENT_OFFSET_MS)
}

// The socket only ever reports `now` and `next`, exactly like the timetable. That is enough to
// learn about a booking, but not to announce its end: `structureBookings` drops a booking from
// `now` five minutes before `bookingUntil`, so it is already gone from the payload by the time the
// end announcement is due. Everything the socket has told us is therefore kept here until the
// booking is well and truly over.
function mergeKnownBookings(
  current: Record<string, KnownBooking>,
  rooms: TimetableState["rooms"],
  roomNumbers: number[],
  now: number,
): Record<string, KnownBooking> {
  const merged = { ...current }
  let hasChanged = false

  for (const roomNumber of roomNumbers) {
    const room = rooms[roomNumber]

    if (!room) {
      continue
    }

    for (const booking of [room.now, room.next]) {
      if (!booking) {
        continue
      }

      const existing = merged[booking.bookingId]

      // A booking can be moved or renamed while we hold it, so refresh rather than skip.
      if (
        existing &&
        existing.bookingFrom.getTime() === booking.bookingFrom.getTime() &&
        existing.bookingUntil.getTime() === booking.bookingUntil.getTime() &&
        existing.bookingUntilNotRounded?.getTime() === booking.bookingUntilNotRounded?.getTime() &&
        existing.roomNumber === roomNumber &&
        existing.roomName === room.name &&
        existing.productName === booking.productName &&
        existing.userGivenName === booking.userGivenName
      ) {
        continue
      }

      merged[booking.bookingId] = {
        bookingId: booking.bookingId,
        roomNumber,
        roomName: room.name,
        productName: booking.productName,
        userGivenName: booking.userGivenName,
        bookingFrom: booking.bookingFrom,
        bookingUntil: booking.bookingUntil,
        bookingUntilNotRounded: booking.bookingUntilNotRounded,
      }
      hasChanged = true
    }
  }

  for (const booking of Object.values(merged)) {
    if (booking.bookingUntil.getTime() < now - KEEP_ON_SCREEN_MS) {
      delete merged[booking.bookingId]
      hasChanged = true
    }
  }

  return hasChanged ? merged : current
}

function readStoredValue<T>(key: string, parse: (value: string) => T, fallback: T): T {
  try {
    const stored = window.localStorage.getItem(key)
    return stored === null ? fallback : parse(stored)
  } catch {
    // Storage can be unavailable (private windows, locked-down kiosk profiles). Not worth
    // reporting, the defaults are fine.
    return fallback
  }
}

function writeStoredValue(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value)
  } catch {
    // See `readStoredValue`.
  }
}

export function useAnnouncements() {
  const rooms = useTimetableStore((state) => state.rooms)
  const playerStates = useTimetableStore((state) => state.playerStates)
  const roomNumbers = useTimetableStore((state) => state.params.rooms)

  const audioRef = useRef<AnnouncementAudio>()

  if (!audioRef.current) {
    audioRef.current = new AnnouncementAudio()
  }

  const audio = audioRef.current

  const [now, setNow] = useState(() => Date.now())
  const [isArmed, setIsArmed] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)

  const [volume, setVolume] = useState(() =>
    readStoredValue(VOLUME_STORAGE_KEY, (value) => Number(value), 1),
  )
  const [isMuted, setIsMuted] = useState(() =>
    readStoredValue(MUTED_STORAGE_KEY, (value) => value === "true", false),
  )
  const [enabled, setEnabled] = useState<EnabledAnnouncements>(() =>
    readStoredValue(
      ENABLED_STORAGE_KEY,
      (value) => ({ ...DEFAULT_ENABLED, ...(JSON.parse(value) as Partial<EnabledAnnouncements>) }),
      DEFAULT_ENABLED,
    ),
  )

  const [knownBookings, setKnownBookings] = useState<Record<string, KnownBooking>>({})

  // When the projector said each film stopped. A booking only counts as ended once it has been
  // seen playing first: a projector sits stopped before the film starts too, and announcing then
  // would tell a room its film is over as it walks in.
  const [filmEndedAt, setFilmEndedAt] = useState<Record<string, number>>({})
  const hasPlayedRef = useRef<Set<string>>(new Set())

  // Mirrored into state so the list re-renders, kept in refs so the ticker can read the latest
  // values without being re-created every second.
  const playedRef = useRef<Record<string, PlayedAnnouncement>>({})
  const [played, setPlayed] = useState<Record<string, PlayedAnnouncement>>({})

  const knownBookingsRef = useRef(knownBookings)
  const filmEndedAtRef = useRef(filmEndedAt)
  const playerStatesRef = useRef(playerStates)
  const isArmedRef = useRef(isArmed)
  const isMutedRef = useRef(isMuted)
  const enabledRef = useRef(enabled)

  knownBookingsRef.current = knownBookings
  filmEndedAtRef.current = filmEndedAt
  playerStatesRef.current = playerStates
  isArmedRef.current = isArmed
  isMutedRef.current = isMuted
  enabledRef.current = enabled

  useEffect(() => {
    setKnownBookings((current) => {
      const merged = mergeKnownBookings(current, rooms, roomNumbers, Date.now())

      // A page can stay open for days, so everything remembered per booking is pruned along
      // with the bookings themselves rather than growing without end.
      if (merged !== current) {
        playedRef.current = Object.fromEntries(
          Object.entries(playedRef.current).filter(([key]) => merged[key.split(":")[0]]),
        )
        setPlayed(playedRef.current)

        setFilmEndedAt((endings) =>
          Object.keys(endings).every((bookingId) => merged[bookingId])
            ? endings
            : Object.fromEntries(
                Object.entries(endings).filter(([bookingId]) => merged[bookingId]),
              ),
        )

        for (const bookingId of hasPlayedRef.current) {
          if (!merged[bookingId]) {
            hasPlayedRef.current.delete(bookingId)
          }
        }
      }

      return merged
    })
  }, [rooms, roomNumbers])

  useEffect(() => {
    const seenAt = Date.now()

    setFilmEndedAt((current) => {
      let next = current

      for (const [bookingId, player] of Object.entries(playerStates)) {
        if (PLAYING_STATES.includes(player.state)) {
          hasPlayedRef.current.add(bookingId)
        } else if (
          STOPPED_STATES.includes(player.state) &&
          hasPlayedRef.current.has(bookingId) &&
          next[bookingId] === undefined
        ) {
          // The projector is polled every two minutes, so this is when we heard about it rather
          // than the moment the film actually stopped.
          next = { ...next, [bookingId]: seenAt }
        }
      }

      return next
    })
  }, [playerStates])

  useEffect(() => {
    audio.setVolume(isMuted ? 0 : volume)
  }, [audio, volume, isMuted])

  useEffect(() => {
    const handleTick = () => {
      const timestamp = Date.now()
      setNow(timestamp)

      if (!isArmedRef.current) {
        return
      }

      void audio.ensureRunning()

      for (const booking of Object.values(knownBookingsRef.current)) {
        for (const type of ANNOUNCEMENT_TYPES) {
          if (!enabledRef.current[type]) {
            continue
          }

          const key = getAnnouncementKey(booking.bookingId, type)

          if (playedRef.current[key]) {
            continue
          }

          // Not due yet, or so long past that saying it now would only confuse.
          const at = getMoment(booking, type, filmEndedAtRef.current, playerStatesRef.current)

          if (!at) {
            continue
          }

          const overdueBy = timestamp - at.getTime()

          if (overdueBy < 0 || overdueBy > CATCH_UP_MS) {
            continue
          }

          // Claimed straight away, so a second tick cannot fire the same clip twice. Recorded
          // even while muted, so unmuting does not replay a backlog.
          playedRef.current = {
            ...playedRef.current,
            [key]: { muted: isMutedRef.current },
          }
          setPlayed(playedRef.current)

          if (isMutedRef.current) {
            continue
          }

          audio
            .play([CHIME_URL, getClipUrl(type, booking.roomNumber)])
            .then((startsAt) => {
              playedRef.current = { ...playedRef.current, [key]: { muted: false, startsAt } }
              setPlayed(playedRef.current)
            })
            .catch((e) => {
              // Hand the moment back so a later tick can try again while it is still in the
              // catch-up window - a clip that failed to load was never spoken.
              const { [key]: _failed, ...rest } = playedRef.current
              playedRef.current = rest
              setPlayed(playedRef.current)

              logError(e, { tags: { roomNumber: booking.roomNumber, type } })
              setError("An announcement could not be played, see Sentry for details.")
            })
        }
      }
    }

    handleTick()

    return createTicker(handleTick)
  }, [audio])

  // Decode the clips for this location up front so the first announcement is not held up by a
  // fetch. Failures are not fatal, `schedule` loads on demand as well.
  useEffect(() => {
    if (!isArmed) {
      return
    }

    const urls = new Set(
      roomNumbers.flatMap((roomNumber) =>
        ANNOUNCEMENT_TYPES.map((type) => getClipUrl(type, roomNumber)),
      ),
    )
    urls.add(TEST_CLIP_URL)
    urls.add(CHIME_URL)

    for (const url of urls) {
      audio.preload(url).catch((e) => logError(e, { tags: { url } }))
    }
  }, [audio, isArmed, roomNumbers])

  const arm = useCallback(async () => {
    try {
      await audio.arm()
      setIsArmed(true)
      setError(undefined)
    } catch (e) {
      logError(e)
      setError(e instanceof Error ? e.message : "Audio could not be started.")
    }
  }, [audio])

  const test = useCallback(() => {
    audio.play([CHIME_URL, TEST_CLIP_URL]).catch((e) => {
      logError(e)
      setError("The test clip could not be played, see Sentry for details.")
    })
  }, [audio])

  const changeVolume = useCallback((value: number) => {
    setVolume(value)
    writeStoredValue(VOLUME_STORAGE_KEY, String(value))
  }, [])

  const toggleMuted = useCallback(() => {
    setIsMuted((current) => {
      writeStoredValue(MUTED_STORAGE_KEY, String(!current))
      return !current
    })
  }, [])

  const toggleEnabled = useCallback((type: AnnouncementType) => {
    setEnabled((current) => {
      const next = { ...current, [type]: !current[type] }
      writeStoredValue(ENABLED_STORAGE_KEY, JSON.stringify(next))
      return next
    })
  }, [])

  const bookings = useMemo<BookingRow[]>(() => {
    const toAnnouncement = (
      booking: KnownBooking,
      type: AnnouncementType,
    ): Announcement | undefined => {
      const at = getMoment(booking, type, filmEndedAt, playerStates)

      if (!at) {
        return undefined
      }

      if (!enabled[type]) {
        return { at, status: "off" }
      }

      const record = played[getAnnouncementKey(booking.bookingId, type)]

      const status: AnnouncementStatus = record
        ? record.muted
          ? "muted"
          : "played"
        : // Already past without ever being said: the page was not open, or not started, in time.
        at.getTime() < now - CATCH_UP_MS
        ? "missed"
        : "pending"

      return { at, status }
    }

    return Object.values(knownBookings)
      .sort((a, b) => a.bookingFrom.getTime() - b.bookingFrom.getTime())
      .map((booking) => ({
        bookingId: booking.bookingId,
        roomNumber: booking.roomNumber,
        roomName: booking.roomName,
        productName: booking.productName,
        userGivenName: booking.userGivenName,
        isRunning: booking.bookingFrom.getTime() <= now && booking.bookingUntil.getTime() > now,
        start: toAnnouncement(booking, "start") as Announcement,
        filmEnd: toAnnouncement(booking, "filmEnd"),
        end: toAnnouncement(booking, "end") as Announcement,
      }))
  }, [knownBookings, played, enabled, filmEndedAt, playerStates, now])

  return {
    bookings,
    now,
    isArmed,
    isSupported: AnnouncementAudio.isSupported(),
    error,
    volume,
    isMuted,
    enabled,
    arm,
    test,
    changeVolume,
    toggleMuted,
    toggleEnabled,
  }
}
